// PreferencesPanel — the author's own preferences, pure.
//
// The genuinely-real preferences, resisting bloat (every row earns its place):
//   - Appearance (theme) → SegmentedControl {light, dark}. Theme is a real
//     per-device preference that lives OUTSIDE the event log and must apply
//     the instant it changes (memory project_theme_outside_event_log), so it
//     is apply-on-change with no Save gate. "System" lives in the left
//     menu's Theme row (pen board 32); here the control shows the palette
//     painted now, so one segment is always lit.
//   - Language → Select over the supported-locale table, each language
//     named by its endonym (never translated, D13). The default is derived from the OS/UI locale at startup
//     (i18n/setup.ts detectLocale) unless the author has picked one; a pick
//     applies AND persists on change (the host wires this to `setLocale`).
//
// DELIBERATELY OMITTED:
//   - Notifications. No notification system exists in the product yet (the
//     Switch story's "Email notifications" is sample copy, not a real
//     feature), so a notifications toggle would be inventing a setting for a
//     capability that isn't there — bloat. Add it when a notification channel
//     is real, not before.
//   - Model. Model selection is not settled (memory todo_ui_model_selection):
//     the "work faster ↔ higher quality" abstraction is the leading candidate
//     but no product ruling has landed. A "coming soon" placeholder row is
//     itself an unmade decision on display (adding-easy/removing-hard) — it
//     was dropped rather than left signposted. Add the real row when the
//     product ruling lands.
//
// Parse-don't-validate at the control boundary: SegmentedControl/Select report
// a bare `string`; a small guard narrows it back to the named union before the
// typed callback fires (no `as` cast — the boundary parses).
//
// Composition: SettingsSection + SettingsRow + SegmentedControl + Select +
// Badge. Register: near-monochrome, no accent spent on a preference control.
//
// Pure/props-driven: the current values arrive as `values`; the panel authors
// its own captions/options via Lingui and holds no state; each change reports
// UP through its own callback.

import { useLingui } from "@lingui/react/macro";
import { endonym, isLocale, locales } from "../../../i18n/supportedLocales.js";
import { SegmentedControl } from "../../SegmentedControl/SegmentedControl.js";
import { Select } from "../../Select/Select.js";
import { SettingsRow } from "../SettingsRow/SettingsRow.js";
import { SettingsSection } from "../SettingsSection/SettingsSection.js";
import type {
    LanguagePreference,
    PreferenceValues,
    ThemePreference,
} from "../types.js";
import "./PreferencesPanel.css";

export type PreferencesPanelProps = {
    readonly values: PreferenceValues;
    readonly onThemeChange: (theme: ThemePreference) => void;
    readonly onLanguageChange: (language: LanguagePreference) => void;
};

// Boundary parsers: the controls hand back a plain string; narrow it to the
// named union with a guard, so an unexpected value is dropped rather than
// cast. Only the two positions the control actually offers are accepted here
// — "system" stays a valid `ThemePreference` for the storage seam, but this
// panel never emits it.
const parseTheme = (value: string): ThemePreference | undefined =>
    value === "light" || value === "dark" ? value : undefined;

export const PreferencesPanel = ({
    values,
    onThemeChange,
    onLanguageChange,
}: PreferencesPanelProps) => {
    const { t } = useLingui();

    const themeOptions = [
        { value: "light", label: t`Light` },
        { value: "dark", label: t`Dark` },
    ];

    const languageOptions = locales.map((l) => ({
        value: l,
        label: endonym(l),
    }));

    return (
        <SettingsSection
            title={t`Preferences`}
            description={t`How the app looks and speaks on this device.`}
        >
            <SettingsRow
                label={t`Appearance`}
                description={t`Light or dark.`}
                control={
                    <SegmentedControl
                        options={themeOptions}
                        value={values.theme}
                        onChange={(v) => {
                            const parsed = parseTheme(v);
                            if (parsed !== undefined) onThemeChange(parsed);
                        }}
                        ariaLabel={t`Appearance`}
                    />
                }
            />

            <SettingsRow
                label={t`Language`}
                description={t`The language of the interface.`}
                control={
                    <Select
                        options={languageOptions}
                        value={values.language}
                        onValueChange={(v) => {
                            if (isLocale(v)) onLanguageChange(v);
                        }}
                        ariaLabel={t`Language`}
                    />
                }
            />
        </SettingsSection>
    );
};
